import { useQueryClient } from '@tanstack/react-query';
import { Check, Pencil, Plus, Trash } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { MemoryFactDTO, ProjectDTO } from '@harness/shared';
import { Modal, PageHeader, Spinner, Switch, SwitchRow } from '../../components/ui';
import { api, download, errorText } from '../../lib/api';
import { qk } from '../../lib/keys';
import { useMemory, type ProjectDetail } from '../../lib/queries';
import { toast } from '../../lib/store';

const SOURCE_TAG = { agent: 'tag-accent', auto: 'tag-outline', user: 'tag-neutral' } as const;

export default function MemoryPage({ project }: { project: ProjectDetail }) {
  const qc = useQueryClient();
  const mem = useMemory(project.id);
  const [newFact, setNewFact] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [topK, setTopK] = useState(project.memoryTopK);
  const [wipe, setWipe] = useState(false);
  useEffect(() => setTopK(project.memoryTopK), [project.memoryTopK]);

  const reload = () => qc.invalidateQueries({ queryKey: qk.memory(project.id) });
  const run = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      await reload();
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };
  const patchProject = async (body: Partial<ProjectDTO>) => {
    try {
      await api.patch(`/api/projects/${project.id}`, body);
      await qc.invalidateQueries({ queryKey: qk.projects });
      await qc.invalidateQueries({ queryKey: qk.project(project.id) });
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  const add = () => {
    const text = newFact.trim();
    if (text.length < 2) return;
    setNewFact('');
    void run(() => api.post(`/api/projects/${project.id}/memory`, { text }));
  };
  const saveEdit = () => {
    const id = editId;
    if (!id) return;
    setEditId(null);
    void run(() => api.patch(`/api/projects/${project.id}/memory/${id}`, { text: editText.trim() }));
  };
  const toggle = (f: MemoryFactDTO) => {
    qc.setQueryData(qk.memory(project.id), (d: typeof mem.data) => d && { ...d, facts: d.facts.map((x) => (x.id === f.id ? { ...x, enabled: !f.enabled } : x)) });
    void run(() => api.patch(`/api/projects/${project.id}/memory/${f.id}`, { enabled: !f.enabled }));
  };

  const facts = mem.data?.facts ?? [];
  const proposals = mem.data?.proposals ?? [];
  // Proposals are grouped by the conversation they came from.
  const groups = proposals.reduce<Record<string, typeof proposals>>((acc, p) => {
    const k = p.conversationTitle ?? 'a conversation';
    (acc[k] ??= []).push(p);
    return acc;
  }, {});

  return (
    <div className="settings-content narrow">
      <PageHeader title="Project memory" sub={`Short facts every agent in ${project.name} can recall. Memory never leaves this project.`}>
        <SwitchRow on={project.memoryEnabled} onChange={(v) => void patchProject({ memoryEnabled: v })}>
          Memory on
        </SwitchRow>
      </PageHeader>

      {Object.entries(groups).map(([title, items]) => (
        <div key={title} className="tile tile-cream proposals">
          <div className="tile-label" style={{ fontSize: 11 }}>Proposed from “{title}”</div>
          {items.map((p) => (
            <div key={p.id} className="proposal-row">
              <span style={{ flex: 1, minWidth: 200, color: 'var(--color-text)' }}>{p.text}</span>
              <button className="btn btn-secondary" onClick={() => void run(() => api.post(`/api/projects/${project.id}/memory/proposals/${p.id}/keep`))}>
                <Check className="icon" />
                Keep
              </button>
              <button className="btn btn-ghost" onClick={() => void run(() => api.del(`/api/projects/${project.id}/memory/proposals/${p.id}`))}>
                Dismiss
              </button>
            </div>
          ))}
        </div>
      ))}

      <div className="card card-pad stack-sm">
      <div className="row" style={{ flexWrap: 'nowrap' }}>
        <input
          className="input"
          placeholder="Add a fact, e.g. “Feature flags live in LaunchDarkly project ldg-web”"
          aria-label="New fact"
          value={newFact}
          onChange={(e) => setNewFact(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && add()}
        />
        <button className="btn btn-primary" style={{ flex: 'none' }} onClick={add} disabled={newFact.trim().length < 2}>
          <Plus className="icon" />
          Add
        </button>
      </div>

      <div style={{ opacity: project.memoryEnabled ? 1 : 0.6 }}>
        {mem.isLoading && (
          <div style={{ padding: 16 }}>
            <Spinner />
          </div>
        )}
        {!mem.isLoading && facts.length === 0 && <div className="empty" style={{ marginTop: 4 }}>No facts yet. Agents save facts with the remember tool, and new conversations propose facts for you to keep.</div>}
        {facts.map((f) => (
          <div key={f.id} className="fact-row">
            <Switch on={f.enabled} onChange={() => toggle(f)} label={f.enabled ? 'Disable fact' : 'Enable fact'} />
            {editId === f.id ? (
              <input
                className="input"
                autoFocus
                aria-label="Edit fact"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveEdit();
                  if (e.key === 'Escape') setEditId(null);
                }}
              />
            ) : (
              <div style={{ opacity: f.enabled ? 1 : 0.5 }}>{f.text}</div>
            )}
            <span className={`tag ${SOURCE_TAG[f.source]}`}>{f.source}</span>
            <div style={{ display: 'flex', gap: 2 }}>
              {editId === f.id ? (
                <button className="btn btn-icon sm" title="Save" onClick={saveEdit}>
                  <Check className="icon" />
                </button>
              ) : (
                <button
                  className="btn btn-icon sm"
                  title="Edit"
                  onClick={() => {
                    setEditId(f.id);
                    setEditText(f.text);
                  }}
                >
                  <Pencil className="icon" />
                </button>
              )}
              <button className="btn btn-icon sm" title="Delete" onClick={() => void run(() => api.del(`/api/projects/${project.id}/memory/${f.id}`))}>
                <Trash className="icon" />
              </button>
            </div>
          </div>
        ))}
      </div>
      </div>

      <div className="card card-pad topk-card">
        <div className="field">
          <label htmlFor="m-topk">Facts retrieved per turn (top-k) · {topK}</label>
          <input id="m-topk" type="range" min={3} max={25} step={1} value={topK} onChange={(e) => setTopK(Number(e.target.value))} onPointerUp={() => void patchProject({ memoryTopK: topK })} onKeyUp={() => void patchProject({ memoryTopK: topK })} />
          <div className="hint">
            {facts.filter((f) => f.enabled).length} of {facts.length} facts on · the most relevant {topK} are injected into each turn
          </div>
        </div>
        <div className="row" style={{ alignItems: 'flex-end', flexWrap: 'nowrap' }}>
          <button className="btn btn-secondary" onClick={() => download(`/api/projects/${project.id}/memory/export`)}>
            Export
          </button>
          <button className="btn btn-secondary btn-danger" onClick={() => setWipe(true)} disabled={facts.length === 0 && proposals.length === 0}>
            Wipe memory
          </button>
        </div>
      </div>

      <Modal open={wipe} onClose={() => setWipe(false)} label="Wipe memory">
        <div className="dialog-title">Wipe all memory for {project.name}?</div>
        <div className="dialog-body">Deletes every fact and pending proposal. Conversations are kept.</div>
        <div className="dialog-actions">
          <button className="btn btn-ghost" onClick={() => setWipe(false)}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              setWipe(false);
              void run(() => api.del(`/api/projects/${project.id}/memory`)).then(() => toast('Memory wiped'));
            }}
          >
            Wipe memory
          </button>
        </div>
      </Modal>
    </div>
  );
}
